import type { SearchableCategory } from "@/components/secondaryCarouselNavigationBar/secondaryCarouselNavigationToggleButton";
import pom from "@/utils/pom";

const CATEGORY_TEST_ID_PREFIX = "category-";

function carouselLinks(root: ParentNode): HTMLAnchorElement[] {
  const container = root.querySelector(
    pom.header.navigationHorizontalScrollContainer
  );
  if (!container) return [];

  return Array.from(
    container.querySelectorAll<HTMLAnchorElement>(
      `ol a[data-testid^="${CATEGORY_TEST_ID_PREFIX}"]`
    )
  );
}

function categoryName(link: HTMLAnchorElement): string {
  return (link.dataset.testid ?? "").slice(CATEGORY_TEST_ID_PREFIX.length);
}

export function readFragmentCategories(
  root: ParentNode
): SearchableCategory[] {
  return carouselLinks(root)
    .map(categoryName)
    .filter((name) => name.length > 0)
    .map((name, index) => ({ id: `${index}-${name}`, name }));
}

export function findFragmentCategoryLink(
  root: ParentNode,
  name: string
): HTMLAnchorElement | undefined {
  return carouselLinks(root).find((link) => categoryName(link) === name);
}

const ACTIVE_LABEL_CLASS = "font-bold";
const INACTIVE_LABEL_CLASS = "font-normal";

function visibleLabel(link: HTMLAnchorElement): Element | null {
  return link.querySelector(":scope > div > span:last-child");
}

export function markActiveFragmentCategory(
  root: ParentNode,
  name: string
): HTMLAnchorElement | undefined {
  const links = carouselLinks(root);
  const activeLink = links.find((link) => categoryName(link) === name);
  if (!activeLink) return undefined;

  for (const link of links) {
    const label = visibleLabel(link);
    const isActive = link === activeLink;
    label?.classList.toggle(ACTIVE_LABEL_CLASS, isActive);
    label?.classList.toggle(INACTIVE_LABEL_CLASS, !isActive);
  }
  return activeLink;
}

export function categoryNameFromTarget(
  root: ParentNode,
  target: EventTarget | null
): string | undefined {
  if (!(target instanceof Element)) return undefined;
  const link = carouselLinks(root).find((candidate) =>
    candidate.contains(target)
  );
  return link ? categoryName(link) : undefined;
}